import { Dayjs } from "dayjs";

/**
 * A contiguous free window the master can book into, expressed as absolute
 * timestamps (each Dayjs carries its own offset). Working windows and off-hours
 * windows are treated identically — the admin/master may book at any free time.
 */
export type FreeWindow = { start: Dayjs; end: Dayjs };

/**
 * Suggest duration-stepped start times across a set of free windows.
 *
 * Works in absolute `Dayjs` timestamps rather than minute-of-day integers. This
 * is what fixes the off-hours midnight bug: a window like 20:00–00:00 has an
 * `end` whose local time is 00:00, so minute-of-day math (`end.hour()*60+...`)
 * collapsed it to 0 and produced "no available time". Comparing absolute
 * instants avoids that entirely.
 *
 * @param windows   free windows to slot up
 * @param duration  appointment length in minutes
 * @param notBefore optional "not in the past" clamp (only relevant for today)
 */
export const buildTimeSlots = (
    windows: FreeWindow[],
    duration: number,
    notBefore?: Dayjs
): Dayjs[] => {
    if (!duration || duration <= 0) return [];

    const slots: Dayjs[] = [];

    for (const window of windows) {
        // Step from max(window.start, notBefore) by `duration`.
        let slot =
            notBefore && notBefore.isAfter(window.start)
                ? notBefore
                : window.start;

        // `!isAfter` is `isSameOrBefore` — the isSameOrBefore plugin is not
        // loaded in this project, so use the core method.
        while (!slot.add(duration, "minute").isAfter(window.end)) {
            slots.push(slot);
            slot = slot.add(duration, "minute");
        }
    }

    return slots;
};

/**
 * True when a manually-entered start time fits entirely inside some free window
 * and is not in the past.
 *
 * @param candidate the proposed start timestamp
 * @param windows   free windows to validate against
 * @param duration  appointment length in minutes
 * @param notBefore optional "not in the past" clamp (only relevant for today)
 */
export const isManualTimeValid = (
    candidate: Dayjs,
    windows: FreeWindow[],
    duration: number,
    notBefore?: Dayjs
): boolean => {
    if (!duration || duration <= 0) return false;
    if (notBefore && candidate.isBefore(notBefore)) return false;

    const candidateEnd = candidate.add(duration, "minute");

    // `[candidate, candidate+duration] ⊆ window` — expressed with core methods
    // (isSameOrAfter/isSameOrBefore plugins are not loaded).
    return windows.some(
        (window) =>
            !candidate.isBefore(window.start) &&
            !candidateEnd.isAfter(window.end)
    );
};
